import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Info } from 'lucide-react';
import type { BoardMetrics, BoardSnapshot } from '@kaba/shared';
import { api } from '../../lib/api';
import { toast } from '../../lib/toast';
import { Button, cx } from '../ui/ui';

const RANGES = [
  [30, '30 Tage'],
  [90, '90 Tage'],
  [180, '180 Tage'],
  [365, '1 Jahr'],
] as const;

const fmtDays = (d: number | null) => (d === null ? '–' : `${d.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ${d === 1 ? 'Tag' : 'Tage'}`);

/** Flow-Kennzahlen eines Boards: Durchlaufzeit, Durchsatz, Alter offener Karten, Blockaden */
export function MetricsView({ snapshot, onOpenCard, canEdit = false }: { snapshot: BoardSnapshot; onOpenCard: (id: string) => void; canEdit?: boolean }) {
  const [days, setDays] = useState<number>(30);
  const { data: m, isLoading, error } = useQuery({
    queryKey: ['metrics', snapshot.board.id, days],
    queryFn: () => api.get<BoardMetrics>(`/api/boards/${snapshot.board.id}/metrics?days=${days}`),
    staleTime: 60_000,
  });
  const colTitle = new Map(snapshot.columns.map((c) => [c.id, c.title]));
  const knownWeeks = m ? m.throughput.filter((w) => w.count !== null) : [];
  const lastColumn = snapshot.columns.length > 1 ? snapshot.columns.at(-1) : undefined;

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col gap-5 overflow-y-auto px-4 py-4 pb-28">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex-1 text-lg font-semibold">Kennzahlen</h2>
        <div role="group" aria-label="Zeitraum" className="flex rounded-lg bg-black/5 p-0.5 dark:bg-white/10">
          {RANGES.map(([d, label]) => (
            <button
              key={d}
              aria-pressed={days === d}
              onClick={() => setDays(d)}
              className={cx('min-h-9 rounded-md px-3 text-sm', days === d ? 'bg-[var(--panel)] font-medium shadow-sm' : 'text-[var(--ink-soft)]')}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-sm text-[var(--ink-soft)]">Berechne …</p>}
      {error && <p className="text-sm text-rose-600">Kennzahlen konnten nicht geladen werden.</p>}
      {m && (
        <>
          {/* ---------- KPI-Zeile ---------- */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile
              label="Durchlaufzeit (Median)"
              value={fmtDays(m.cycleTime.median)}
              note={
                m.cycleTime.count
                  ? `85 % in höchstens ${fmtDays(m.cycleTime.p85)} · ${m.cycleTime.count} ${m.cycleTime.count === 1 ? 'Karte' : 'Karten'}${m.cycleTime.unknown ? ` · ${m.cycleTime.unknown} nicht berechenbar` : ''}`
                  : m.cycleTime.unknown
                    ? `${m.cycleTime.unknown} erledigt, Arbeitsbeginn nicht belegt`
                    : 'Noch keine erledigten Karten'
              }
            />
            <Tile
              label="Durchsatz"
              value={knownWeeks.length ? `${(m.completed / knownWeeks.length).toLocaleString('de-DE', { maximumFractionDigits: 1 })} / Woche` : '–'}
              note={`${m.completed} erledigt in ${knownWeeks.length} ${knownWeeks.length === 1 ? 'Woche' : 'Wochen'}${m.reopened ? ` · ${m.reopened} wieder geöffnet` : ''}`}
            />
            <Tile label="In Arbeit" value={String(m.openStarted)} note={`${m.openNotStarted} noch nicht begonnen`} />
            <Tile
              label="Blockiert"
              value={String(m.blocked.current.length)}
              note={`${fmtDays(m.blocked.totalDays)} Blockadezeit im Zeitraum`}
              warn={m.blocked.current.length > 0}
            />
          </div>

          {m.openInLastColumn > 0 && lastColumn && (
            <LastColumnHint boardId={snapshot.board.id} column={lastColumn} count={m.openInLastColumn} canEdit={canEdit} />
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Erledigt je Woche" hint={`Kalenderwochen ab Montag (${m.timeZone}); angeschnittene Wochen sind markiert`}>
              <ThroughputChart data={m.throughput} />
            </Panel>
            <Panel title="Ø Verweildauer je Spalte" hint="Erledigte Karten im Zeitraum, bis zur Erledigung">
              {m.timeInColumn.length ? (
                <HBars rows={m.timeInColumn.map((t) => ({ key: t.columnId, label: t.title, value: t.avgDays, text: fmtDays(t.avgDays), sub: `${t.cards} Karten` }))} />
              ) : (
                <Empty />
              )}
            </Panel>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Älteste offene Karten" hint="Alter seit Arbeitsbeginn">
              {m.oldestOpen.length ? (
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-[var(--ink-soft)]">
                    <tr>
                      <th className="py-1 font-medium">Karte</th>
                      <th className="py-1 font-medium">Spalte</th>
                      <th className="py-1 text-right font-medium">Alter</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--line)]">
                    {m.oldestOpen.map((c) => (
                      <tr key={c.cardId}>
                        <td className="py-1.5 pr-2">
                          <button className="min-h-8 text-left hover:underline" onClick={() => onOpenCard(c.cardId)}>
                            {c.blocked && <Ban size={13} className="mr-1 inline text-rose-600" aria-label="blockiert" />}
                            {c.title}
                          </button>
                        </td>
                        <td className="py-1.5 pr-2 text-[var(--ink-soft)]">{colTitle.get(c.columnId) ?? ''}</td>
                        <td className="py-1.5 text-right tabular-nums">{fmtDays(c.ageDays)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Empty text="Keine begonnenen offenen Karten." />
              )}
            </Panel>

            <Panel title="Belegung je Spalte" hint="Offene Karten jetzt, mit WIP-Limit">
              <ul className="flex flex-col gap-2.5">
                {m.wip.map((w) => {
                  const max = Math.max(w.limit ?? 0, ...m.wip.map((x) => x.count), 1);
                  const over = w.limit !== null && w.count > w.limit;
                  return (
                    <li key={w.columnId} className="flex flex-col gap-1">
                      <div className="flex items-baseline gap-2 text-sm">
                        <span className="flex-1 truncate">{w.title}</span>
                        <span className={cx('tabular-nums', over && 'font-semibold')}>
                          {w.count}
                          {w.limit ? ` / ${w.limit}` : ''}
                        </span>
                        {over && (
                          <span className="inline-flex items-center gap-1 rounded bg-rose-600 px-1.5 text-xs font-medium text-white">
                            <AlertTriangle size={12} aria-hidden /> über Limit
                          </span>
                        )}
                      </div>
                      <div className="relative h-2 rounded-full bg-[var(--chart-grid)]" aria-hidden>
                        <div
                          className={cx('h-2 rounded-full', over ? 'bg-rose-600' : 'bg-[var(--chart-1)]')}
                          style={{ width: `${(w.count / max) * 100}%` }}
                        />
                        {w.limit !== null && (
                          <div className="absolute -top-1 h-4 w-0.5 bg-[var(--ink-soft)]" style={{ left: `calc(${(w.limit / max) * 100}% - 1px)` }} />
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Panel>
          </div>

          {m.blocked.current.length > 0 && (
            <Panel title="Gerade blockiert">
              <ul className="flex flex-col divide-y divide-[var(--line)] text-sm">
                {m.blocked.current.map((b) => (
                  <li key={b.cardId} className="flex items-center gap-2 py-1.5">
                    <Ban size={14} className="shrink-0 text-rose-600" aria-hidden />
                    <button className="min-h-8 flex-1 text-left hover:underline" onClick={() => onOpenCard(b.cardId)}>
                      {b.title}
                      {b.reason && <span className="text-[var(--ink-soft)]"> – {b.reason}</span>}
                    </button>
                    <span className="tabular-nums text-[var(--ink-soft)]">seit {fmtDays(b.sinceDays)}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <FlowStages snapshot={snapshot} stages={m.stages} canEdit={canEdit} />

          <p className="flex gap-2 text-xs text-[var(--ink-soft)]">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>
              Erledigt heißt überall in KaBa: der Erledigt-Haken ist gesetzt. Durchlaufzeit: vom ersten Eintritt in eine Spalte „in Arbeit“ bis
              zum Haken – ohne belegten Arbeitsbeginn „nicht berechenbar“, nie 0. 85 % aller Karten waren in höchstens dieser Zeit fertig. Wieder
              geöffnete Karten zählen nicht im Durchsatz. Wochen vor Anlage des Boards sind unbekannt. Blockadezeit zählt die Zeiträume, in denen
              Karten als blockiert markiert waren.
            </span>
          </p>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, note, warn }: { label: string; value: string; note: string; warn?: boolean }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3">
      <span className="text-xs font-medium text-[var(--ink-soft)]">{label}</span>
      <span className="flex items-center gap-1.5 text-2xl font-semibold tabular-nums">
        {warn && <Ban size={18} className="text-rose-600" aria-label="Achtung" />}
        {value}
      </span>
      <span className="text-xs text-[var(--ink-soft)]">{note}</span>
    </div>
  );
}

function Panel({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-4" aria-label={title}>
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <p className="text-xs text-[var(--ink-soft)]">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Empty({ text = 'Noch keine Daten im Zeitraum.' }: { text?: string }) {
  return <p className="text-sm text-[var(--ink-soft)]">{text}</p>;
}

/* ---------- Säulen: erledigt je Woche (eine Reihe, keine Legende) ---------- */

function ThroughputChart({ data }: { data: BoardMetrics['throughput'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...data.map((d) => d.count ?? 0));
  const niceMax = max <= 4 ? max : Math.ceil(max / 2) * 2;
  const ticks = niceMax <= 4 ? Array.from({ length: niceMax + 1 }, (_, i) => i) : [0, niceMax / 2, niceMax];
  const H = 160;
  const slot = 100 / data.length; // Prozent je Woche
  const total = data.reduce((a, d) => a + (d.count ?? 0), 0);
  if (!total) return <Empty text="Im Zeitraum wurde noch nichts erledigt." />;

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* y-Achse */}
        <div className="relative w-6 shrink-0 text-right text-[11px] text-[var(--ink-soft)]" style={{ height: H }} aria-hidden>
          {ticks.map((t) => (
            <span key={t} className="absolute right-0 -translate-y-1/2 tabular-nums" style={{ top: H - (t / niceMax) * H }}>
              {t}
            </span>
          ))}
        </div>
        <div className="relative min-w-0 flex-1" style={{ height: H }} onMouseLeave={() => setHover(null)}>
          {/* Raster */}
          {ticks.map((t) => (
            <div key={t} className="absolute inset-x-0 h-px bg-[var(--chart-grid)]" style={{ top: H - (t / niceMax) * H }} aria-hidden />
          ))}
          {/* Säulen */}
          <div className="absolute inset-0 flex items-end" aria-hidden>
            {data.map((d, i) => (
              <div
                key={d.weekStart}
                className="flex h-full flex-1 items-end justify-center"
                onMouseEnter={() => setHover(i)}
                onClick={() => setHover(i)}
              >
                {d.count === null ? (
                  // Vor Anlage des Boards: unbekannt, nicht 0
                  <div className="h-full w-full bg-[var(--chart-grid)] opacity-50" />
                ) : (
                  <div
                    className={cx('rounded-t-[4px] bg-[var(--chart-1)] transition-opacity', hover !== null && hover !== i && 'opacity-50', d.partial && 'opacity-60')}
                    style={{ height: `${(d.count / niceMax) * 100}%`, width: 'min(24px, 60%)', minHeight: d.count ? 2 : 0 }}
                  />
                )}
              </div>
            ))}
          </div>
          {hover !== null && data[hover] && (
            <div
              role="status"
              className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-xs whitespace-nowrap text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
              style={{ left: `${(hover + 0.5) * slot}%`, top: Math.max(0, H - ((data[hover]!.count ?? 0) / niceMax) * H - 44) }}
            >
              Woche ab {data[hover]!.label}: {data[hover]!.count === null ? 'vor Anlage des Boards' : <><strong>{data[hover]!.count}</strong> erledigt{data[hover]!.partial ? ' (angeschnitten)' : ''}</>}
            </div>
          )}
        </div>
      </div>
      {/* x-Achse: nicht jede Beschriftung, damit nichts kollidiert */}
      <div className="ml-8 flex text-[11px] text-[var(--ink-soft)]" aria-hidden>
        {data.map((d, i) => (
          <span key={d.weekStart} className="flex-1 text-center tabular-nums">
            {data.length <= 8 || i % Math.ceil(data.length / 7) === 0 || i === data.length - 1 ? d.label : ''}
          </span>
        ))}
      </div>
      {data.some((d) => d.count === null) && <p className="mt-1 ml-8 text-[11px] text-[var(--ink-soft)]">Graue Fläche: vor Anlage des Boards – keine Daten.</p>}
      {/* Tabelle für Screenreader */}
      <table className="sr-only">
        <caption>Erledigte Karten je Woche</caption>
        <thead>
          <tr>
            <th>Woche ab</th>
            <th>Erledigt</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.weekStart}>
              <td>{d.label}{d.partial ? ' (angeschnitten)' : ''}</td>
              <td>{d.count ?? 'unbekannt'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Waagerechte Balken (eine Reihe, Werte direkt beschriftet) ---------- */

function HBars({ rows }: { rows: { key: string; label: string; value: number; text: string; sub?: string }[] }) {
  const max = Math.max(...rows.map((r) => r.value), 0.1);
  return (
    <ul className="flex flex-col gap-2.5">
      {rows.map((r) => (
        <li key={r.key} className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-2 text-sm" title={`${r.label}: ${r.text}${r.sub ? ` (${r.sub})` : ''}`}>
          <span className="truncate">{r.label}</span>
          <div className="h-3.5" aria-hidden>
            <div className="h-3.5 rounded-r-[4px] bg-[var(--chart-1)]" style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }} />
          </div>
          <span className="text-right tabular-nums">{r.text}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---------- Letzte Spalte ohne Haken (M01) ---------- */

function LastColumnHint({ boardId, column, count, canEdit }: { boardId: string; column: { id: string; title: string }; count: number; canEdit: boolean }) {
  const [done, setDone] = useState(false);
  const create = async () => {
    try {
      await api.post(`/api/boards/${boardId}/automations`, {
        name: `Beim Ziehen in „${column.title}“ erledigen`,
        trigger: { type: 'moved', columnId: column.id },
        conditions: [],
        actions: [{ type: 'complete' }],
      });
      setDone(true);
      toast.info('Automation angelegt: Karten werden beim Ziehen in diese Spalte als erledigt markiert.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <div role="note" className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
      <AlertTriangle size={16} className="shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        {count} {count === 1 ? 'Karte liegt' : 'Karten liegen'} in „{column.title}“ ohne Erledigt-Haken und {count === 1 ? 'zählt' : 'zählen'} deshalb als offen. Erledigt heißt
        überall in KaBa: Haken gesetzt.
      </span>
      {canEdit && !done && (
        <Button size="sm" onClick={() => void create()}>
          Künftig beim Ziehen erledigen
        </Button>
      )}
    </div>
  );
}

/* ---------- Auswertung: welche Spalten bedeuten „in Arbeit“ (M01) ---------- */

function FlowStages({ snapshot, stages, canEdit }: { snapshot: BoardSnapshot; stages: Record<string, 'backlog' | 'active'>; canEdit: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const set = async (columnId: string, stage: 'backlog' | 'active') => {
    setBusy(columnId);
    try {
      // Erst alle Spalten ausdrücklich festlegen, damit der Vorschlag nicht mitwandert
      for (const c of snapshot.columns) {
        const want = c.id === columnId ? stage : stages[c.id];
        if (c.flowStage !== want) await api.patch(`/api/columns/${c.id}`, { flowStage: want });
      }
      await qc.invalidateQueries({ queryKey: ['metrics', snapshot.board.id] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const explicit = snapshot.columns.some((c) => c.flowStage);
  return (
    <details className="rounded-xl border border-[var(--line)] bg-[var(--panel)] p-4">
      <summary className="min-h-8 cursor-pointer text-sm font-semibold">Auswertung: Wann beginnt die Arbeit?</summary>
      <p className="mt-2 text-xs text-[var(--ink-soft)]">
        Karten in Spalten „Noch nicht begonnen“ zählen nicht als laufende Arbeit. Die Durchlaufzeit beginnt beim ersten Eintritt in eine Spalte „In Arbeit“.
        {explicit ? '' : ' Vorschlag: nur die erste Spalte gilt als noch nicht begonnen.'}
      </p>
      <ul className="mt-3 flex flex-col divide-y divide-[var(--line)]">
        {snapshot.columns.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
            <span className="min-w-0 flex-1 truncate">{c.title}</span>
            {canEdit ? (
              <select
                aria-label={`Auswertung für „${c.title}“`}
                className="min-h-10 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 sm:min-h-8"
                value={stages[c.id] ?? 'active'}
                disabled={!!busy}
                onChange={(e) => void set(c.id, e.target.value as 'backlog' | 'active')}
              >
                <option value="backlog">Noch nicht begonnen</option>
                <option value="active">In Arbeit</option>
              </select>
            ) : (
              <span className="text-[var(--ink-soft)]">{stages[c.id] === 'backlog' ? 'Noch nicht begonnen' : 'In Arbeit'}</span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
